import * as S from './styles';

import PanelOptionMixin from '../mixin';

import {
    DefaultDropdownBox,
    SelectedBox,
} from '@/components/Common/DropdownBox';
import ComparisonTableIcon from '@svgIcons/table_linetype.svg';

import { PANEL_OPTION_KEYS } from '@/utils/consts';

import { DATE_GROUP_ROWS } from '@/utils/dashboard';
import { isValueExist } from '@/utils/utils';

const { BREAKDOWN, TABLE_TRANSPOSE_TOGGLE } = PANEL_OPTION_KEYS;

const COMPARISON_OPTIONS = [
    { id: 'none', name: 'None' },
    { id: 'selected_period', name: 'Selected period range' },
    { id: 'day', name: 'Daily breakdown' },
    { id: 'week', name: 'Weekly breakdown (Start:Mon)' },
    { id: 'weekSunSat', name: 'Weekly breakdown (Start:Sun)' },
    { id: 'quarter', name: 'Quarterly breakdown' },
    { id: 'month', name: 'Monthly breakdown' },
];

export default {
    name: 'WidgetSettingPanelGa4MappingOption',
    mixins: [PanelOptionMixin],
    props: {
        isTransposed: { type: Boolean, default: false },
        breakdownList: {
            type: Array,
            default() {
                return [];
            },
        },
        columnDimensionIds: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    data() {
        return {
            dropdownVisible: false,
        };
    },
    watch: {
        isComparisonDateFilterExist: {
            handler(newVal) {
                if (!newVal && this.dropdownValue.id === 'selected_period') {
                    this.handleComparisonChange({
                        value: this.options[0],
                    });
                }
            },
            immediate: true,
        },
    },
    computed: {
        selectedBreakdowns: R.compose(R.propOr([], 'breakdownList')),
        firstSelectedBreakdown() {
            return this.selectedBreakdowns[0] || {};
        },
        switchDisabled() {
            return DATE_GROUP_ROWS.includes(this.firstSelectedBreakdown.name);
        },
        hasMultipleBreakdowns() {
            return this.selectedBreakdowns.length > 1;
        },
        selectedComparisonOption() {
            // comparisonByDate가 true이면 'selected_period' 반환
            if (this.comparisonByDate) {
                const option = COMPARISON_OPTIONS.find(
                    (option) => option.id === 'selected_period'
                );
                return option || COMPARISON_OPTIONS[0];
            }

            // columnDimensionIds가 설정되어 있으면 해당 옵션 반환
            if (this.columnDimensionIds && this.columnDimensionIds.length > 0) {
                const dimensionId = this.columnDimensionIds[0];
                const option = COMPARISON_OPTIONS.find(
                    (option) => option.id === dimensionId
                );
                return option || COMPARISON_OPTIONS[0];
            }

            // 둘 다 아니면 'none' 반환
            return COMPARISON_OPTIONS[0];
        },
        dropdownValue() {
            const selected = this.selectedComparisonOption;
            return selected;
        },
        isComparisonDateFilterExist() {
            return (
                isValueExist(this.widgetComparisonDateFilter) ||
                isValueExist(this.dateFilterProps.comparisonDateFilter)
            );
        },
        options() {
            let options = COMPARISON_OPTIONS;

            if (!this.isComparisonDateFilterExist) {
                options = options.filter(
                    (option) => option.id !== 'selected_period'
                );
            }

            return options;
        },
    },
    methods: {
        handleComparisonChange(option) {
            const { value } = option;

            if (value.id === 'none') {
                // None 선택시 comparisonByDate를 false로 설정하고 columnDimensionIds 초기화
                this.$emit('update', false);
                this.$emit('updateColumnDimensionIds', []);
                return;
            }

            if (value.id === 'selected_period') {
                // Selected period range 선택시 comparisonByDate를 true로 설정
                if (this.hasMultipleBreakdowns || this.isTransposed) {
                    return this.renderDialog(() => {
                        this.$emit('update', true);
                        this.$emit('updateColumnDimensionIds', []);
                    });
                }
                this.$emit('update', true);
                this.$emit('updateColumnDimensionIds', []);
                return;
            }

            // 날짜 breakdown 옵션들 (day, week, weekSunSat, weekDate, month)
            const dimensionId = value.id;

            if (this.hasMultipleBreakdowns || this.isTransposed) {
                return this.renderDialog(() => {
                    this.$emit('update', false);
                    this.$emit('updateColumnDimensionIds', [dimensionId]);
                });
            }
            this.$emit('update', false);
            this.$emit('updateColumnDimensionIds', [dimensionId]);
        },
        renderDialog(confirmCallback) {
            const { hasMultipleBreakdowns, isTransposed } = this;

            const text = [
                hasMultipleBreakdowns &&
                    this.$t(
                        'NEW_DASHBOARD.comparison_by_date_confirm_modal_description'
                    ),
                isTransposed &&
                    this.$t(
                        'NEW_DASHBOARD.force_table_transpose_toggle_off_desc'
                    ),
            ]
                .filter(Boolean)
                .join('\n');

            return this.dialog({
                text,
                confirmCb: () => {
                    if (hasMultipleBreakdowns) {
                        this.$emit('updateByKey', {
                            key: BREAKDOWN,
                            value: [this.firstSelectedBreakdown],
                        });
                    }

                    if (isTransposed) {
                        this.$emit('updateByKey', {
                            key: TABLE_TRANSPOSE_TOGGLE,
                            value: false,
                        });
                    }

                    confirmCallback();
                },
                confirmText: this.$t('COMMON.confirm'),
            });
        },
        formatter(option) {
            if (option.id === 'none') {
                return this.$t('NEW_DASHBOARD.none');
            }

            if (option.id === 'selected_period') {
                return this.$t('NEW_DASHBOARD.comparison_selected_period');
            }

            return this.$t(`METRICS.${option.id}`);
        },
    },
    render() {
        const { switchDisabled } = this;

        return (
            <S.ComponentWrapper>
                {this.wrapTooltip({
                    visible: switchDisabled,
                    title: this.$t(
                        'NEW_DASHBOARD.comparison_by_date_disable_tooltip'
                    ),
                    content: (
                        <div class="top-wrapper">
                            <div class="title">
                                {/* <ComparisonTableIcon class="ga4utm-icon" /> */}
                                <h1>
                                    {this.$t(
                                        'NEW_DASHBOARD.comparison_by_date_option_title'
                                    )}
                                </h1>
                            </div>
                            <div class="dropdown-wrapper">
                                <SelectedBox
                                    selectedOption={this.dropdownValue}
                                    translator={this.formatter}
                                    disabled={switchDisabled}
                                    onClick={() => {
                                        if (!switchDisabled) {
                                            this.dropdownVisible =
                                                !this.dropdownVisible;
                                        }
                                    }}
                                />
                                <DefaultDropdownBox
                                    selectedOption={this.dropdownValue}
                                    optionList={this.options}
                                    disabled={switchDisabled}
                                    isVisible={this.dropdownVisible}
                                    onConfirm={(option) => {
                                        this.handleComparisonChange({
                                            value: option,
                                        });
                                        this.dropdownVisible = false;
                                    }}
                                    onClickOutside={() => {
                                        this.dropdownVisible = false;
                                    }}
                                    translator={this.formatter}
                                    width={300}
                                />
                            </div>
                        </div>
                    ),
                })}
            </S.ComponentWrapper>
        );
    },
};
